/*
 * Copyright (c) New Cloud Technologies, Ltd., 2013-2024
 *
 * You can not use the contents of the file in any way without New Cloud Technologies, Ltd. written permission.
 * To obtain such a permit, you should contact New Cloud Technologies, Ltd. at https://myoffice.ru/contacts/
 *
 */

import type { RPC } from 'frame-rpc';
import createRPC from 'frame-rpc';

import type { DOCUMENT_TYPES_VALUES, FORMATS_VALUES, OPEN_FORMATS_VALUES } from './constants';
import {
    AMR_API_VERSION,
    DOCUMENT_TYPES,
    OPEN_FORMATS,
    SAVE_FORMAT_NAMES,
    SAVE_FORMATS,
    STABLE_VARIANTS,
} from './constants';
import type {
    BaseCommands,
    BaseEvents,
    ErrorInfo,
    OpenDocumentParams,
    OpenParametersWithoutMode,
    TypeInfo,
} from './types';
import type { ReadOnlyParams } from './AmrReadonly/types';

type Unsubscribe = () => void;

export abstract class BaseAmrApi<TCommands extends BaseCommands, TEvents extends BaseEvents> {
    private static experimentalFeaturesEnabled = false;
    #frameRpc: RPC.Instance<TCommands>;
    #subscribes = new Map<keyof TEvents, Set<(response: any) => void>>();

    protected constructor(
        frame: HTMLIFrameElement,
        origin: string,
        private readonly supportedEvents: (keyof TEvents)[],
    ) {
        this.#frameRpc = createRPC<TCommands, TEvents>(
            window,
            frame.contentWindow!,
            origin ?? window.location.origin,
            this.createEventHandlers(),
        );
    }

    private createEventHandlers() {
        return this.supportedEvents.reduce((methods, event) => {
            methods[event] = this.invoke.bind(this, event);
            return methods;
        }, {} as RPC.EventHandlers<TEvents>);
    }

    destroy() {
        this.#frameRpc.destroy();
    }

    protected invoke(action: keyof TEvents, payload: unknown) {
        const subscribers = this.#subscribes.get(action);
        if (subscribers) {
            subscribers.forEach((cb) => cb(payload));
        }
    }

    protected request<CMD extends keyof TCommands>(command: CMD, data: TCommands[CMD]): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            this.#frameRpc.call(command, data, (response: ArrayBuffer | ErrorInfo) => {
                if (response && 'errorInfo' in response) {
                    reject(response.errorInfo);
                } else {
                    resolve(response);
                }
            });
        });
    }

    protected subscribe<EVENT extends keyof TEvents>(
        notification: EVENT,
        callback: (response: TEvents[EVENT]) => void,
    ): Unsubscribe {
        let subscribers = this.#subscribes.get(notification);
        if (!subscribers) {
            subscribers = new Set();
            this.#subscribes.set(notification, subscribers);
        }

        subscribers.add(callback);

        //unsubscribe
        return () => {
            this.#subscribes.get(notification)?.delete(callback);
        };
    }

    protected open(params: OpenDocumentParams): Promise<TypeInfo> {
        return new Promise((resolve, reject) => {
            const ext = params.name.substring(params.name.lastIndexOf('.') + 1);
            if (!params.type) {
                return reject(`Unknown document type "${ext}"`);
            }
            this.request('openDocument', params)
                .then(() => {
                    resolve({ type: params.type, format: params.format } as TypeInfo);
                })
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.error(error);
                    reject(error);
                });
        });
    }

    protected getOpenParameters(params: ReadOnlyParams): OpenParametersWithoutMode {
        const ext = params.ext || params.filename.substring(params.filename.lastIndexOf('.') + 1);
        const typeInfo = BaseAmrApi.getOptions().EXTENSION_TO_TYPE[ext.toLowerCase()];
        return {
            apiVersion: AMR_API_VERSION,
            ...params,
            name: params.filename,
            type: typeInfo?.type,
            format: typeInfo?.format,
            experimentalFeaturesEnabled: BaseAmrApi.getExperimentalFeaturesEnabled(),
        };
    }
    /**
     * Подписка на фатальные ошибки при открытии, сохранении или изменении документа.
     * @param callback - Колбэк-функция, срабатывающая при ошибке
     * @returns - Колбэк-функция для отписки от ошибок
     */
    onError(callback: (payload: string) => void) {
        return this.subscribe('error', ({ errorInfo }) => callback(errorInfo));
    }

    /**
     * Подписка на запрос на перезагрузку редактора.
     * @param callback - Колбэк-функция, срабатывающая при запросе на перезагрузку
     * @returns Колбэк-функция для отписки от события перезагрузки
     */
    onPageReloadRequested(callback: () => void) {
        return this.subscribe('pageReloadRequested', callback);
    }

    /**
     * Отключает перехват фокуса редактором.
     * Возвращает `Promise`, но дожидаться его разрешения не обязательно.
     */
    async blur(): Promise<void> {
        await this.request('blur', void 0);
    }

    static getOptions() {
        const EXTENSION_TO_TYPE = STABLE_VARIANTS.reduce<
            Record<string, { type: DOCUMENT_TYPES_VALUES; format: OPEN_FORMATS_VALUES }>
        >((result, variant) => {
            variant.ext.forEach((ext) => {
                result[ext] = {
                    type: variant.type,
                    format: variant.format,
                };
            });

            return result;
        }, {});

        const SUPPORTED_MIME_TYPES = Object.keys(EXTENSION_TO_TYPE).map((ext) => `.${ext}`);

        return {
            STABLE_VARIANTS,
            EXTENSION_TO_TYPE,
            SUPPORTED_MIME_TYPES,
            SAVE_FORMAT_NAMES,
            SAVE_DOCUMENT_FORMATS: SAVE_FORMATS,
        };
    }

    // TODO getSaveDocumentOptions - костыль для getDocumentExtension. Исправлеяет баг WTE-36651
    //  Будет удален при рефакторинге getDocumentExtension. WTE-36789
    static getSaveDocumentOptions() {
        return STABLE_VARIANTS.reduce<Record<string, { type: DOCUMENT_TYPES_VALUES; format: OPEN_FORMATS_VALUES }>>(
            (result, variant) => {
                const extensions = variant.ext;
                if (extensions?.length === 0) {
                    return result;
                }

                const defaultExtension = extensions[0];

                result[defaultExtension] = {
                    type: variant.type,
                    format: variant.format,
                };

                return result;
            },
            {},
        );
    }

    /**
     * Получение расширения документа
     * @param documentType - Тип документа
     * @param format - Формат документа
     * @param defaultExt - Расширение по-умолчанию
     * @returns Расширение документа
     */
    // TODO Логика в getDocumentExtension требует рефакторинга (WTE-36789).
    //  См. комментарии getSaveDocumentOptions и STABLE_VARIANTS.
    static getDocumentExtension(documentType: DOCUMENT_TYPES_VALUES, format: FORMATS_VALUES, defaultExt: string) {
        const extensionToType = BaseAmrApi.getSaveDocumentOptions();
        if (format === OPEN_FORMATS.pdf || format === OPEN_FORMATS.pdfa) {
            return DOCUMENT_TYPES.pdf;
        } else if (
            defaultExt &&
            extensionToType[defaultExt] &&
            extensionToType[defaultExt].type === documentType &&
            extensionToType[defaultExt].format === format
        ) {
            return defaultExt;
        }

        return Object.keys(extensionToType).find(
            (ext) => extensionToType[ext].type === documentType && extensionToType[ext].format === format,
        );
    }

    /**
     * Включение/выключение экспериментальных функций
     */
    static setExperimentalFeaturesEnabled(value: boolean) {
        BaseAmrApi.experimentalFeaturesEnabled = value;
    }

    /**
     * Получение статуса экспериментальных функций (включено/выключено)
     */
    static getExperimentalFeaturesEnabled() {
        return BaseAmrApi.experimentalFeaturesEnabled;
    }

    /**
     * Проверка iframe элемента и наличия contentWindow
     */
    static checkIFrameElement(element: HTMLIFrameElement) {
        return element.tagName === 'IFRAME' && element.contentWindow !== null;
    }
}

export default BaseAmrApi;
